import { useEffect, useState, useCallback } from 'react';
import API from '@/lib/api';

const LANGS = [
  { code: 'nl', label: '🇳🇱 NL' },
  { code: 'fr', label: '🇫🇷 FR' },
  { code: 'en', label: '🇬🇧 EN' },
  { code: 'de', label: '🇩🇪 DE' },
];

const TONES = [
  { code: 'professioneel', label: '💼 Professioneel' },
  { code: 'vriendelijk', label: '😊 Vriendelijk' },
  { code: 'kordaat', label: '⚡ Kordaat' },
  { code: 'formeel', label: '🎩 Formeel' },
];

/**
 * Preview-popup voor "Verstuur per mail" knop op alle document-types.
 * Met:
 *  - Mailbox + ontvanger + cc
 *  - Bewerkbaar onderwerp + body
 *  - Taalkeuze (NL/FR/EN/DE) — default is de KLANTTAAL (uit klantenfiche)
 *  - AI-verbeter knop met toon-selectie
 *  - HTML preview / tekst-bewerk modus
 */
export default function MailPreviewModal({ docId, lang: initialLang = 'auto', onClose, onSent }) {
  const [lang, setLang] = useState(initialLang);
  const [klantLang, setKlantLang] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [polishTone, setPolishTone] = useState('professioneel');
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [polishHistory, setPolishHistory] = useState([]); // undo-stack

  const loadPreview = useCallback(async (lng) => {
    setLoading(true);
    try {
      // Stuur 'auto' bij eerste load → backend gebruikt klant.taal als default
      const r = await API.post('/email/preview-doc-email', { doc_id: docId, lang: lng });
      setPreview(r.data);
      setSubject(r.data.subject || '');
      setBody(r.data.html_body || '');
      if (!to) setTo(r.data.to || '');
      // Backend retourneert de gebruikte taal — neem die over zodat de UI-knop correct gemarkeerd is
      if (r.data.lang && r.data.lang !== lang) setLang(r.data.lang);
      if (r.data.klant_lang) setKlantLang(r.data.klant_lang);
    } catch (e) {
      alert('Preview kon niet geladen worden: ' + (e.response?.data?.detail || e.message));
      onClose();
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, onClose]);

  useEffect(() => { loadPreview(lang); }, [lang, loadPreview]);

  const changeLang = (newLang) => {
    if (newLang === lang) return;
    if (polishHistory.length > 0 || body !== preview?.html_body) {
      if (!window.confirm('Taal wijzigen overschrijft je bewerkingen. Doorgaan?')) return;
    }
    setPolishHistory([]);
    setLang(newLang);
  };

  const doPolish = async () => {
    if (!body.trim()) {
      alert('De body is leeg, niets om te verbeteren.');
      return;
    }
    setPolishing(true);
    try {
      const r = await API.post('/email/ai-polish-doc', {
        html_body: body,
        lang,
        toon: polishTone,
        doc_id: docId,
      });
      const newHtml = (r.data?.polished_html || '').trim();
      if (newHtml && newHtml !== body) {
        setPolishHistory(h => [...h, body]);
        setBody(newHtml);
      } else {
        alert('AI gaf geen verbetering terug.');
      }
    } catch (e) {
      alert('AI-verbeter mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setPolishing(false);
  };

  const undoPolish = () => {
    if (polishHistory.length === 0) return;
    const last = polishHistory[polishHistory.length - 1];
    setBody(last);
    setPolishHistory(h => h.slice(0, -1));
  };

  const doSend = async () => {
    if (!to || !to.includes('@')) {
      alert('Geldig e-mailadres vereist');
      return;
    }
    if (!subject.trim()) {
      if (!window.confirm('Geen onderwerp ingevuld. Toch verzenden?')) return;
    }
    setSending(true);
    try {
      const ccList = cc.split(/[,;]/).map(s => s.trim()).filter(s => s.includes('@'));
      const r = await API.post('/email/verstuur-met-pdf', {
        doc_id: docId,
        lang,
        to,
        subject_override: subject,
        body_override: body,
        cc: ccList,
      });
      alert(`E-mail verstuurd naar ${to}${r.data.pdf_attached ? ' (met PDF)' : ''}`);
      onSent?.();
      onClose();
    } catch (e) {
      alert('Verzenden mislukt: ' + (e.response?.data?.detail || e.message));
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={() => !sending && onClose()} data-testid="mail-preview-modal">
      <div className="bg-white shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <span>📧</span> Voorbeeld vóór verzenden
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">Kies taal, verbeter met AI en controleer vóór verzenden — handtekening wordt automatisch toegevoegd.</p>
          </div>
          <button onClick={() => !sending && onClose()} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="mail-preview-close">×</button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm text-gray-400">⏳ Voorbeeld laden...</div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {/* Mailbox + Taalkeuze */}
              <div className="flex items-center gap-3 text-xs bg-purple-50 border border-purple-200 rounded p-2">
                <span className="text-purple-700 shrink-0">📮 Vanuit:</span>
                <span className="font-mono text-gray-700 truncate">{preview?.account_email || '-'}</span>
                {preview?.has_signature && (
                  <span className="text-[10px] font-bold uppercase bg-green-100 text-green-700 px-1.5 py-0.5 rounded shrink-0">✓ Handtekening</span>
                )}
                <div className="ml-auto flex items-center gap-1 shrink-0">
                  <span className="text-[10px] text-gray-500 mr-1">Taal:</span>
                  {LANGS.map(L => (
                    <button
                      key={L.code}
                      onClick={() => changeLang(L.code)}
                      className={`px-1.5 py-0.5 rounded text-[11px] font-bold relative ${lang === L.code ? 'bg-[#002FA7] text-white' : 'bg-white border border-gray-300 hover:border-[#002FA7]'}`}
                      data-testid={`mail-preview-lang-${L.code}`}
                      title={klantLang === L.code ? 'Voorkeurtaal van deze klant' : ''}
                    >
                      {L.label}
                      {klantLang === L.code && (
                        <span className="absolute -top-1 -right-1 text-[8px] bg-green-500 text-white rounded-full w-3 h-3 inline-flex items-center justify-center font-bold leading-none" title="Voorkeurtaal van klant">★</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Aan */}
              <div>
                <label className="text-[10px] font-bold uppercase text-gray-500">Aan</label>
                <input
                  className="field-input mt-1 w-full"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="klant@bedrijf.be"
                  data-testid="mail-preview-to"
                />
                {preview?.to_name && <span className="text-[10px] text-gray-400 mt-0.5 block">{preview.to_name}</span>}
              </div>

              {/* CC */}
              <div>
                <label className="text-[10px] font-bold uppercase text-gray-500">Cc (optioneel)</label>
                <input
                  className="field-input mt-1 w-full"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="boekhouding@bedrijf.be, ..."
                  data-testid="mail-preview-cc"
                />
                {/* Auto-CC: factoring email (zichtbaar tonen vóór verzenden) */}
                {(preview?.cc_auto || []).length > 0 && (
                  <div className="mt-2 p-2 rounded bg-orange-50 border border-orange-300" data-testid="mail-preview-cc-auto">
                    <div className="text-[10px] font-bold uppercase text-orange-800 mb-1">🏦 Automatisch CC (IBAN Factoring)</div>
                    {preview.cc_auto.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-orange-900">
                        <span className="font-semibold">{c.email}</span>
                        <span className="text-[10px] text-orange-600">— {c.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* IBAN Factoring banner (waarschuwing dat factoring actief is voor deze factuur) */}
              {preview?.factoring_active && (
                <div className="p-3 rounded-lg bg-orange-50 border-l-4 border-orange-500" data-testid="mail-preview-factoring-banner">
                  <div className="text-xs font-bold text-orange-900 flex items-center gap-1">
                    🏦 IBAN Factoring is actief voor deze klant
                  </div>
                  <div className="text-[11px] text-orange-700 mt-1 leading-relaxed">
                    Deze factuur wordt verstuurd via factoring (IBAN 2). De factuur-PDF bevat onderaan een cessie-tekst,
                    een introductiebrief wordt als extra bijlage meegestuurd, en de factoringmaatschappij ontvangt
                    automatisch een kopie via CC.
                  </div>
                </div>
              )}

              {/* Onderwerp */}
              <div>
                <label className="text-[10px] font-bold uppercase text-gray-500">Onderwerp</label>
                <input
                  className="field-input mt-1 w-full"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="mail-preview-subject"
                />
              </div>

              {/* AI verbeter toolbar */}
              <div className="flex items-center gap-2 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded p-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase text-purple-700 shrink-0">✨ AI verbeter</span>
                <select
                  value={polishTone}
                  onChange={(e) => setPolishTone(e.target.value)}
                  className="text-xs px-2 py-1 border border-purple-300 rounded bg-white"
                  data-testid="mail-preview-tone"
                >
                  {TONES.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
                <button
                  onClick={doPolish}
                  disabled={polishing}
                  className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-xs rounded font-bold inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="mail-preview-polish-btn"
                >
                  {polishing ? '⏳ Bezig...' : '✨ Verbeter mij'}
                </button>
                {polishHistory.length > 0 && (
                  <button
                    onClick={undoPolish}
                    className="px-2 py-1 bg-white border border-gray-300 text-xs rounded text-gray-700 hover:bg-gray-50"
                    data-testid="mail-preview-undo-btn"
                  >
                    ↶ Ongedaan ({polishHistory.length})
                  </button>
                )}
                <span className="text-[10px] text-purple-600 ml-auto">Tip: kies eerst de toon, dan klik Verbeter</span>
              </div>

              {/* Body preview/edit */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase text-gray-500">Bericht (incl. handtekening)</label>
                  <button
                    onClick={() => setEditMode(m => !m)}
                    className="text-[10px] font-bold uppercase text-blue-600 hover:text-blue-800"
                    data-testid="mail-preview-toggle-edit"
                  >
                    {editMode ? '👁 Bekijk voorbeeld' : '✏ Bewerk tekst'}
                  </button>
                </div>
                {editMode ? (
                  <textarea
                    className="field-input w-full font-mono text-xs"
                    rows={14}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    data-testid="mail-preview-body-edit"
                  />
                ) : (
                  <div
                    className="border border-gray-200 rounded p-3 bg-white max-h-72 overflow-y-auto text-sm"
                    dangerouslySetInnerHTML={{ __html: body }}
                    data-testid="mail-preview-body-html"
                  />
                )}
              </div>

              {/* Attachments */}
              <div>
                <label className="text-[10px] font-bold uppercase text-gray-500">Bijlagen</label>
                <div className="mt-1 space-y-1">
                  {(preview?.attachments || []).map((a, i) => (
                    <div key={i} className={`flex items-center gap-2 text-xs border rounded px-2 py-1.5 ${a.factoring ? 'bg-orange-50 border-orange-300' : 'bg-gray-50 border-gray-200'}`} data-testid={a.factoring ? 'mail-preview-attachment-factoring' : 'mail-preview-attachment'}>
                      <span>{a.factoring ? '🏦' : '📎'}</span>
                      <span className={`font-mono ${a.factoring ? 'text-orange-900 font-semibold' : 'text-gray-700'}`}>{a.filename}</span>
                      {a.note && <span className={`ml-auto text-[10px] italic ${a.factoring ? 'text-orange-700' : 'text-gray-400'}`}>{a.note}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 bg-gray-50 border-t flex items-center justify-between">
              <div className="text-[10px] text-gray-500">
                💡 Open + click tracking actief
              </div>
              <div className="flex items-center gap-2">
                <button onClick={onClose} disabled={sending} className="btn btn-secondary btn-sm" data-testid="mail-preview-cancel">
                  Annuleer
                </button>
                <button onClick={doSend} disabled={sending || !to} className="btn btn-primary btn-sm inline-flex items-center gap-1.5" data-testid="mail-preview-send">
                  {sending ? '⏳ Verzenden...' : <>✉ Bevestig & Verzenden</>}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
